import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import LoginModal from '../Auth/LoginModal';
import './Navbar.css';
import NoPermissionModal from '../Common/NoPermissionModal';
import NotificationDropdown from '../Common/NotificationDropdown';
import { useNotifications } from '../../contexts/NotificationContext';
import logo from '../../assets/images/Duotutor_logo_white.svg';

const NavigationBar = () => {
    const { user, logout, token } = useContext(AuthContext);
    const { connectSocket, disconnectSocket, fetchUnreadCount, requestNotificationPermission } = useNotifications();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [permissionModal, setShowPermissionModal] = useState({
        show: false,
        requiredRole: '',
        currentRole: ''
    });
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);

    // 로그인 시 Socket.io 연결 및 알림 가져오기
    useEffect(() => {
        if (user && token) {
            connectSocket(token);
            fetchUnreadCount();
            requestNotificationPermission();
        } else {
            disconnectSocket();
        }

        return () => {
            disconnectSocket();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, token]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const closeMenu = () => {
        setIsOpen(false);
        setActiveDropdown(null);
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
        setActiveDropdown(null);
    };

    const toggleDropdown = (dropdownName) => {
        setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    };

    // 데스크톱에서는 호버로 드롭다운을 열고 닫는다 (activeDropdown 단일 상태라 항상 하나만 열림).
    // 모바일(햄버거 메뉴)은 탭 시 mouseenter→click이 충돌하므로 호버 이벤트를 무시하고 클릭 토글만 사용.
    const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

    const handleDropdownEnter = (dropdownName) => {
        if (isDesktop()) setActiveDropdown(dropdownName);
    };

    const handleDropdownLeave = () => {
        if (isDesktop()) setActiveDropdown(null);
    };

    return (
        <>
            <nav className="navbar-custom">
                <div className="container">
                    <Link to="/" className="navbar-brand-custom" onClick={closeMenu}>
                        <img src={logo} alt="DuoTutor" className="logo-image" />
                    </Link>
                    <button
                        className="navbar-toggler"
                        onClick={toggleMenu}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className={`navbar-menu ${isOpen ? 'show' : ''}`}>
                        <div className="nav-links">
                            {!user ? (
                                // 비로그인: 로그인 | 시작하기
                                <>
                                    <Link to="/login" className="nav-link-custom" onClick={closeMenu}>로그인</Link>
                                    <Link to="/signup" className="btn-primary" onClick={closeMenu}>시작하기</Link>
                                </>
                            ) : (
                                // 로그인 상태
                                <>
                                    {/* 1. 학생 메뉴 (학생만 - roleLevel === 2) */}
                                    {user.roleLevel === 2 && (
                                        <div
                                            className={`nav-item dropdown ${activeDropdown === 'student' ? 'show' : ''}`}
                                            onMouseEnter={() => handleDropdownEnter('student')}
                                            onMouseLeave={handleDropdownLeave}
                                        >
                                            <span className="dropdown-toggle" onClick={() => toggleDropdown('student')}>
                                                학생
                                            </span>
                                            <div className={`dropdown-menu ${activeDropdown === 'student' ? 'show' : ''}`}>
                                                <Link to="/students/join-class" className="dropdown-item" onClick={closeMenu}>반 참여하기</Link>
                                                <Link to="/students/projects" className="dropdown-item" onClick={closeMenu}>내 프로젝트</Link>
                                                <Link to="/students/portfolio" className="dropdown-item" onClick={closeMenu}>포트폴리오</Link>
                                            </div>
                                        </div>
                                    )}

                                    {/* 2. 멘토 포털 (관리자 또는 roleLevel >= 3) */}
                                    {(user.isAdmin || user.roleLevel >= 3) && (
                                        <Link to="/instructor/dashboard" className="nav-link-custom" onClick={closeMenu}>멘토 포털</Link>
                                    )}

                                    {/* 2-1. 수업운영 메뉴 (관리자 전용) - 드롭다운 */}
                                    {user.isAdmin && (
                                        <div
                                            className={`nav-item dropdown ${activeDropdown === 'classops' ? 'show' : ''}`}
                                            onMouseEnter={() => handleDropdownEnter('classops')}
                                            onMouseLeave={handleDropdownLeave}
                                        >
                                            <span className="dropdown-toggle" onClick={() => toggleDropdown('classops')}>
                                                수업운영
                                            </span>
                                            <div className={`dropdown-menu ${activeDropdown === 'classops' ? 'show' : ''}`}>
                                                <Link to="/admin/class-operations" className="dropdown-item" onClick={closeMenu}>수업 일정</Link>
                                                <Link to="/admin/class-inquiry" className="dropdown-item" onClick={closeMenu}>수업 문의</Link>
                                                <Link to="/admin/curriculum" className="dropdown-item" onClick={closeMenu}>커리큘럼</Link>
                                                <Link to="/admin/instructors" className="dropdown-item" onClick={closeMenu}>멘토 관리</Link>
                                                <Link to="/admin/instructor-recruitment" className="dropdown-item" onClick={closeMenu}>멘토 모집</Link>
                                            </div>
                                        </div>
                                    )}

                                    {/* 3. 지원 메뉴 (모든 로그인 사용자) */}
                                    <div
                                        className={`nav-item dropdown ${activeDropdown === 'support' ? 'show' : ''}`}
                                        onMouseEnter={() => handleDropdownEnter('support')}
                                        onMouseLeave={handleDropdownLeave}
                                    >
                                        <span className="dropdown-toggle" onClick={() => toggleDropdown('support')}>
                                            지원
                                        </span>
                                        <div className={`dropdown-menu ${activeDropdown === 'support' ? 'show' : ''}`}>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => {
                                                    window.open('https://www.bnveducation.com/', '_blank', 'noopener noreferrer');
                                                    closeMenu();
                                                }}
                                            >
                                                AI Tools
                                            </button>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => {
                                                    window.open('https://www.bnveducation.com/curriculum', '_blank', 'noopener noreferrer');
                                                    closeMenu();
                                                }}
                                            >
                                                외부 링크
                                            </button>
                                        </div>
                                    </div>

                                    {/* 4. 문의 (모든 로그인 사용자) */}
                                    <Link to="/inquiry" className="nav-link-custom" onClick={closeMenu}>문의</Link>

                                    {/* 5. 알림 */}
                                    <NotificationDropdown />

                                    {/* 6. 계정 정보 드롭다운 */}
                                    {user.isAdmin ? (
                                        // 관리자: 관리자 메뉴
                                        <div
                                            className={`nav-item dropdown ${activeDropdown === 'admin' ? 'show' : ''}`}
                                            onMouseEnter={() => handleDropdownEnter('admin')}
                                            onMouseLeave={handleDropdownLeave}
                                        >
                                            <span className="dropdown-toggle" onClick={() => toggleDropdown('admin')}>
                                                관리자
                                            </span>
                                            <div className={`dropdown-menu ${activeDropdown === 'admin' ? 'show' : ''}`}>
                                                <Link to="/admin/overview" className="dropdown-item" onClick={closeMenu}>운영 현황</Link>
                                                <Link to="/admin/users" className="dropdown-item" onClick={closeMenu}>사용자</Link>
                                                <Link to="/admin/schools" className="dropdown-item" onClick={closeMenu}>학교 관리</Link>
                                                <button type="button" className="dropdown-item" onClick={() => { handleLogout(); closeMenu(); }}>로그아웃</button>
                                            </div>
                                        </div>
                                    ) : (
                                        // 일반 사용자: 사용자 이름
                                        <div
                                            className={`nav-item dropdown ${activeDropdown === 'user' ? 'show' : ''}`}
                                            onMouseEnter={() => handleDropdownEnter('user')}
                                            onMouseLeave={handleDropdownLeave}
                                        >
                                            <span className="dropdown-toggle" onClick={() => toggleDropdown('user')}>
                                                {user.name}
                                            </span>
                                            <div className={`dropdown-menu ${activeDropdown === 'user' ? 'show' : ''}`}>
                                                <Link to="/instructor/account" className="dropdown-item" onClick={closeMenu}>계정관리</Link>
                                                <Link to="/students/mcp-tokens" className="dropdown-item" onClick={closeMenu}>MCP</Link>
                                                <button type="button" className="dropdown-item" onClick={() => { handleLogout(); closeMenu(); }}>로그아웃</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
            <LoginModal show={showLoginModal} onHide={() => setShowLoginModal(false)} />
            <NoPermissionModal
                show={permissionModal.show}
                onHide={() => setShowPermissionModal({ ...permissionModal, show: false })}
                requiredRole={permissionModal.requiredRole}
                currentRole={permissionModal.currentRole}
            />
        </>
    );
};

export default NavigationBar;
